import { Injectable, OnDestroy } from "@angular/core";
import { Observable, of, Subscription, timer } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import {
  AnalyticsBatchResponseDto,
  AnalyticsEventDto,
} from "src/app/data/http/dto/analytics/analytics-event-dto.model";
import { AnalyticsHTTPService } from "src/app/data/http/analytics-http.service";
import {
  createAnalyticsId,
  getAnalyticsAnonymousId,
  getAnalyticsSessionId,
} from './analytics-identity';

const FLUSH_INTERVAL_MS = 30000;
const MAX_BATCH_SIZE = 20;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
  private queue: AnalyticsEventDto[] = [];
  private flushInProgress = false;
  private timerSubscription: Subscription;

  constructor(
    private analyticsHttpService: AnalyticsHTTPService,
  ) {
    this.timerSubscription = timer(
      FLUSH_INTERVAL_MS,
      FLUSH_INTERVAL_MS
    ).subscribe(() => this.flush().subscribe());
  }

  ngOnDestroy(): void {
    this.timerSubscription.unsubscribe();
  }

  track(
    name: string,
    properties: Record<string, unknown> = {},
    options: { immediate?: boolean } = {},
  ): void {
    const sessionId = this.getSessionId();
    const event: AnalyticsEventDto = {
      source: 'web',
      name,
      session_id: sessionId,
      anonymous_id: getAnalyticsAnonymousId(),
      idempotency_key: this.createIdempotencyKey(sessionId, name),
      occurred_at: new Date().toISOString(),
      properties,
      context: this.buildContext(),
    };

    this.queue.push(event);

    if (options.immediate || this.queue.length >= MAX_BATCH_SIZE) {
      this.flush().subscribe();
    }
  }

  trackPageView(path: string, title?: string): void {
    this.track('web.page_view', {
      path,
      ...(title ? { title } : {}),
    }, { immediate: true });
  }

  trackHomeWatched(path: string): void {
    this.track('web.home.watched', {
      path,
    }, { immediate: true });
  }

  flush(): Observable<AnalyticsBatchResponseDto | null> {
    if (this.flushInProgress || this.queue.length === 0) {
      return of(null);
    }

    const events = this.queue.splice(0, MAX_BATCH_SIZE);
    this.flushInProgress = true;

    return this.analyticsHttpService.trackBatch(events).pipe(
      catchError(() => {
        return of(null);
      }),
      finalize(() => {
        this.flushInProgress = false;
      })
    );
  }

  private buildContext(): Record<string, unknown> {
    return {
      path: window.location.pathname,
      language: navigator.language,
      screen: `${window.screen.width}x${window.screen.height}`,
      device_type: this.getDeviceType(),
      user_agent: navigator.userAgent,
    };
  }

  private getDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    const hasTouch = navigator.maxTouchPoints > 0;
    const screenWidth = Math.min(window.screen.width, window.screen.height);

    if (/ipad|tablet/.test(userAgent) || (hasTouch && screenWidth >= 768)) {
      return 'tablet';
    }

    if (
      /android|iphone|ipod|blackberry|iemobile|opera mini/.test(userAgent)
      || (hasTouch && screenWidth < 768)
    ) {
      return 'mobile';
    }

    return 'desktop';
  }

  private getAnonymousId(): string {
    return getAnalyticsAnonymousId();
  }

  private getSessionId(): string {
    return getAnalyticsSessionId();
  }

  private createId(): string {
    return createAnalyticsId();
  }

  private createIdempotencyKey(sessionId: string, name: string): string {
    return `${sessionId}:${name}:${this.createId()}`;
  }
}
